import React, { useState, useEffect, useCallback } from 'react';
import { seedProgressLabel } from '../utils/seedTier';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Seed/element flavor icons (best-effort; falls back to a generic sprout).
const SEED_ICON = {
  Fire: '🔥', Cold: '❄️', Ice: '❄️', Electricity: '⚡', Lightning: '⚡',
  Water: '💧', Earth: '🪨', Air: '🌪️', Acid: '🧪', Sonic: '🔊',
  Dark: '🌑', Light: '☀️', Holy: '✝️', Life: '💚', Death: '💀',
  Energy: '✨', Time: '⏳', Good: '😇', Psionic: '🧠', Telepathy: '🧠',
  Telekinesis: '🌀', Clairvoyance: '👁️', Metabolism: '🫀', Psychokinesis: '💥',
};
const seedIcon = (s) => SEED_ICON[s] || '🌱';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

const SpellbookPanel = ({ onClose, initialSeed = null }) => {
  const [ladder, setLadder] = useState(null);
  const [equipped, setEquipped] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);      // spell name currently toggling
  const [openSeed, setOpenSeed] = useState(initialSeed);
  const [showLocked, setShowLocked] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/player/spellbook`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load spellbook');
      setLadder(data.ladder || []);
      setEquipped(data.equippedSpells || []);
      if (!initialSeed && data.ladder && data.ladder.length) setOpenSeed(data.ladder[0].seed);
    } catch (e) {
      setError(e.message);
    }
  }, [initialSeed]);

  useEffect(() => { load(); }, [load]);

  const toggleEquip = async (name, currentlyEquipped) => {
    if (busy) return;
    setBusy(name);
    setError(null);
    const next = currentlyEquipped ? equipped.filter(s => s !== name) : [...equipped, name];
    try {
      const res = await fetch(`${API_BASE_URL}/api/player/equipped-spells`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ equippedSpells: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update');
      setEquipped(data.equippedSpells || next);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 9600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
  const panel = { width: 'min(760px, 96vw)', maxHeight: '92vh', background: 'linear-gradient(180deg,#161a2b,#0f1220)', border: '1px solid #2a2f4a', borderRadius: '12px', display: 'flex', flexDirection: 'column', color: '#dcdce6', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.9rem 1.1rem', borderBottom: '1px solid #23283f' }}>
          <span style={{ fontSize: '1.3rem' }}>📖</span>
          <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#f0d98c' }}>Spellbook</h2>
          <span style={{ fontSize: '0.72rem', color: '#7a7f9a', marginLeft: '0.25rem' }}>
            Higher tiers hit far harder — level a seed to unlock them, then equip to cast.
          </span>
          <label style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#9aa', display: 'flex', gap: '0.3rem', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={showLocked} onChange={e => setShowLocked(e.target.checked)} />
            Show locked
          </label>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid #333', color: '#ccc', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer' }}>✕</button>
        </div>

        {error && <div style={{ background: 'rgba(231,76,60,0.15)', color: '#ff9b8f', padding: '0.5rem 1.1rem', fontSize: '0.8rem' }}>⚠️ {error}</div>}

        {/* Seed group tabs */}
        {ladder && (
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', padding: '0.6rem 1.1rem', borderBottom: '1px solid #23283f' }}>
            {ladder.map(g => (
              <button key={g.seed} onClick={() => setOpenSeed(g.seed)} style={{
                padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', border: 'none', cursor: 'pointer',
                background: openSeed === g.seed ? '#5b8def' : '#1b1f33',
                color: openSeed === g.seed ? '#fff' : '#8a90ad', fontWeight: openSeed === g.seed ? 'bold' : 'normal',
              }}>
                {seedIcon(g.seed)} {g.seed} <span style={{ opacity: 0.7 }}>Lv {g.seedLevel}</span>
              </button>
            ))}
          </div>
        )}

        {/* Spell list */}
        <div style={{ overflowY: 'auto', padding: '0.6rem 1.1rem 1.1rem' }}>
          {!ladder && !error && <div style={{ color: '#777', padding: '1rem' }}>Loading…</div>}
          {ladder && ladder.filter(g => g.seed === openSeed).map(group => {
            const rows = group.spells.filter(s => showLocked || s.qualified);
            // Equipped state is live (updates on toggle); the ladder's s.equipped is only the
            // initial snapshot from the fetch.
            const equippedSet = new Set(equipped);
            // Best available = highest-mp unlocked spell not yet equipped.
            const bestAvail = [...group.spells].filter(s => s.qualified && !equippedSet.has(s.name)).sort((a, b) => b.mp - a.mp)[0];
            return (
              <div key={group.seed}>
                <div style={{ fontSize: '0.75rem', color: '#8a90ad', margin: '0.3rem 0 0.6rem' }}>
                  {seedIcon(group.seed)} <strong style={{ color: '#cfd3e6' }}>{group.seed}</strong> seed — {seedProgressLabel(group.seedLevel)}
                </div>
                {rows.map(s => {
                  const isEquipped = equippedSet.has(s.name);
                  const isBest = bestAvail && s.name === bestAvail.name;
                  const border = isEquipped ? '#2ecc71' : (s.qualified ? (isBest ? '#f0d98c' : '#2a2f4a') : '#242842');
                  return (
                    <div key={s.name} style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.65rem', marginBottom: '0.4rem',
                      borderRadius: '8px', border: `1px solid ${border}`,
                      background: s.qualified ? 'rgba(40,46,74,0.4)' : 'rgba(20,22,36,0.5)', opacity: s.qualified ? 1 : 0.62,
                    }}>
                      <div style={{ minWidth: 30, textAlign: 'center', fontSize: '0.7rem', color: '#7a7f9a' }}>T{s.tier}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ fontWeight: 'bold', color: s.qualified ? '#ecedf0' : '#8a90ad' }}>{s.name}</span>
                          {isEquipped && <span style={{ fontSize: '0.6rem', color: '#2ecc71', border: '1px solid #2ecc71', borderRadius: '4px', padding: '0 4px' }}>EQUIPPED</span>}
                          {isBest && !isEquipped && <span style={{ fontSize: '0.6rem', color: '#f0d98c', border: '1px solid #f0d98c', borderRadius: '4px', padding: '0 4px' }}>BEST AVAILABLE</span>}
                          {s.multiSeed && <span style={{ fontSize: '0.6rem', color: '#a78bfa' }}>combo</span>}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#8a90ad', marginTop: 2 }}>
                          {s.mp} MP{s.damageText ? ` · ${s.damageText}` : ''}
                        </div>
                        {!s.qualified && (
                          <div style={{ fontSize: '0.68rem', color: '#e0a06a', marginTop: 2 }}>
                            🔒 Needs {s.gatingSeed} seed {s.seedLevelNeeded} (you have {s.seedLevelNow})
                          </div>
                        )}
                      </div>
                      {s.qualified ? (
                        <button
                          onClick={() => toggleEquip(s.name, isEquipped)}
                          disabled={busy === s.name}
                          style={{
                            padding: '0.35rem 0.7rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 'bold',
                            background: isEquipped ? '#3a2020' : '#2ecc71', color: isEquipped ? '#ff9b8f' : '#0d1a10',
                          }}>
                          {busy === s.name ? '…' : isEquipped ? 'Unequip' : 'Equip'}
                        </button>
                      ) : (
                        <div style={{ fontSize: '0.66rem', color: '#6b7089', minWidth: 60, textAlign: 'right' }}>Locked</div>
                      )}
                    </div>
                  );
                })}
                {rows.length === 0 && <div style={{ color: '#666', fontSize: '0.8rem', padding: '0.5rem' }}>No spells to show.</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SpellbookPanel;
